import { ArrowUpRight, Phone, Sparkles } from "lucide-react";
import { Reveal } from "@/lib/reveal";

const handleJump = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden noise"
    >
      {/* Background grid + orbs */}
      <div className="absolute inset-0 hero-grid" aria-hidden="true" />
      <div
        className="absolute -top-24 -left-24 h-[520px] w-[520px] hero-orb"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 right-[-10%] h-[560px] w-[560px] hero-orb-b"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,123,0,0.12),transparent_55%)]"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-36 md:pt-44 pb-24 md:pb-32">
        <Reveal>
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#FFB347]"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
            Performance marketing studio
          </div>
        </Reveal>

        <Reveal delay={120}>
          <h1
            data-testid="hero-heading"
            className="font-display mt-8 text-5xl sm:text-6xl md:text-7xl lg:text-[92px] font-black leading-[0.95] tracking-tighter max-w-5xl"
          >
            Scale smarter with{" "}
            <span className="text-gradient-gold">Mature Marketing</span>
            <span className="text-[#FF7B00]">.</span>
          </h1>
        </Reveal>

        <Reveal delay={220}>
          <p className="mt-6 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed">
            Growth &nbsp;•&nbsp; Branding &nbsp;•&nbsp; Lead Generation.
            Helping businesses scale with proven strategies, high-intent
            funnels, and design systems that convert.
          </p>
        </Reveal>

        <Reveal delay={320}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-get-started"
              onClick={() => handleJump("#contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF7B00] to-[#FF9933] px-7 py-4 text-sm font-bold text-black shadow-[0_0_24px_rgba(255,123,0,0.35)] hover:shadow-[0_0_40px_rgba(255,123,0,0.6)] transition-all hover:scale-[1.03]"
            >
              Get Started
              <ArrowUpRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.25}
              />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => handleJump("#contact")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#FF7B00]/60 bg-transparent px-7 py-[14px] text-sm font-bold text-[#FF7B00] hover:bg-[#FF7B00]/10 transition-colors"
            >
              <Phone className="h-4 w-4" strokeWidth={2.25} />
              Contact Us
            </button>
          </div>
        </Reveal>

        {/* Mini stat row */}
        <Reveal delay={440}>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              { k: "Avg. ROAS", v: "6.2x" },
              { k: "Ads Managed", v: "$4.8M+" },
              { k: "Client Retention", v: "98%" },
              { k: "Industries", v: "24+" },
            ].map((item) => (
              <div
                key={item.k}
                data-testid={`hero-stat-${item.k.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className="border-l-2 border-[#FF7B00]/50 pl-4"
              >
                <div className="font-display text-2xl md:text-3xl font-bold text-white">
                  {item.v}
                </div>
                <div className="text-xs md:text-sm text-zinc-500 uppercase tracking-[0.14em] mt-1">
                  {item.k}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Bottom marquee */}
      <div
        data-testid="hero-marquee"
        className="relative border-y border-white/5 bg-black/40 backdrop-blur-sm py-5 overflow-hidden"
        aria-hidden="true"
      >
        <div className="flex whitespace-nowrap animate-marquee gap-16 font-display text-xl md:text-2xl text-zinc-600">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 pr-16">
              <span>Meta Ads</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Google Ads</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>SEO</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Lead Gen</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Branding</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Web Dev</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Social Media</span>
              <span className="text-[#FF7B00]">✦</span>
              <span>Content</span>
              <span className="text-[#FF7B00]">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
